// Five heart rate zones as a share of a maximum estimated from age, as the Android overview has them.

export const ZONE_COUNT = 5;

/** Where zones 2 to 5 start, as a share of the maximum. */
export const ZONE_THRESHOLDS = [0.6, 0.7, 0.8, 0.9];

export const ZONE_COLORS = ['#3A9CF0', '#47E0D2', '#C6F01F', '#FF8C1A', '#FF2E73'];

/** [bpm, seconds] spent at each heart rate, worked out by the page from every reading. */
export type SecondsByBpm = Array<[number, number]>;

export interface ZoneTimes {
  /** Where zones 2 to 5 start, in bpm. */
  starts: number[];
  /** Seconds in each zone, zone 1 first. */
  seconds: number[];
}

/** Whole years, counting a birthday that hasn't come round yet this year as not yet had. */
export function ageOn(dateOfBirth: string, today: Date): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOfBirth);
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  const hadBirthday = today.getMonth() + 1 > month || (today.getMonth() + 1 === month && today.getDate() >= day);
  return today.getFullYear() - year - (hadBirthday ? 0 : 1);
}

/** From 220 − age, raised to the recording's own peak when it went higher. */
export function zoneStarts(age: number | null, peakBpm = 0): number[] | null {
  if (age === null || age < 1 || age >= 120) return null;
  const maximum = Math.max(220 - age, peakBpm);
  return ZONE_THRESHOLDS.map((share) => Math.round(share * maximum));
}

/** 1 to 5. */
export function zoneOf(bpm: number, starts: number[]): number {
  let zone = 1;
  starts.forEach((start, i) => {
    if (bpm >= start) zone = i + 2;
  });
  return zone;
}

/** "<132", "132–145" or "175+". */
export function zoneRangeLabel(zone: number, starts: number[]): string {
  if (zone <= 1) return `<${starts[0]}`;
  if (zone >= ZONE_COUNT) return `${starts[ZONE_COUNT - 2]}+`;
  return `${starts[zone - 2]}–${starts[zone - 1] - 1}`;
}

export function secondsInZones(secondsByBpm: SecondsByBpm, starts: number[]): number[] {
  const seconds = new Array(ZONE_COUNT).fill(0);
  secondsByBpm.forEach(([bpm, spent]) => {
    seconds[zoneOf(bpm, starts) - 1] += spent;
  });
  return seconds;
}

export function zoneTimes(secondsByBpm: SecondsByBpm, age: number | null, peakBpm: number): ZoneTimes | null {
  const starts = zoneStarts(age, peakBpm);
  if (!starts) return null;
  const seconds = secondsInZones(secondsByBpm, starts);
  return seconds.some((s) => s > 0) ? { starts, seconds } : null;
}

export interface ZoneRow {
  zone: number;
  color: string;
  seconds: number;
  range: string;
  /** Against the longest zone; a sliver for any time at all, so a zone that was touched never looks empty. */
  barPercent: number;
  /** Of all the time, for the stacked bar. */
  share: number;
}

export function zoneRows({ starts, seconds }: ZoneTimes): ZoneRow[] {
  const longest = Math.max(...seconds);
  const total = seconds.reduce((a, b) => a + b, 0);
  return seconds.map((s, i) => ({
    zone: i + 1,
    color: ZONE_COLORS[i],
    seconds: s,
    range: zoneRangeLabel(i + 1, starts),
    barPercent: s > 0 && longest > 0 ? Math.max((s / longest) * 100, 3) : 0,
    share: total > 0 ? s / total : 0,
  }));
}

/** The zone with the most time, and its share of the whole as a whole percentage. */
export function mostlyZone(seconds: number[]): { zone: number; percent: number } | null {
  const total = seconds.reduce((a, b) => a + b, 0);
  if (total <= 0) return null;
  const longest = seconds.indexOf(Math.max(...seconds));
  return { zone: longest + 1, percent: Math.round((seconds[longest] / total) * 100) };
}
